import { stringify } from 'qs';
import SuperFetchV2 from '../../superFetchV2';
import {
  PromotionSchedule,
  PromotionScheduleCreateRequest,
  PromotionScheduleDeleteResponse,
  PromotionScheduleExcelDownloadResponse,
  PromotionScheduleListFilter,
  PromotionScheduleLog,
  PromotionScheduleUpdateRequest,
  PromotionSchedulesFindQuery,
  PromotionSchedulesFindResponse,
  PromotionSchedulesListQuery,
  PromotionSchedulesListResponse,
} from './types';

const BASE = '/promotion-schedules';

/**
 * 프로모션 스케줄 API 클라이언트.
 * 백엔드 apps/admin/src/promotion-schedule 과 1:1 대응.
 */
export const promotionScheduleApi = {
  findAll: (params: PromotionSchedulesFindQuery): Promise<PromotionSchedulesFindResponse> =>
    SuperFetchV2.get(BASE, {
      params,
      paramsSerializer: (p) => stringify(p, { arrayFormat: 'comma' }),
    }),

  /**
   * 리스트 조회 (AMZBM-110) — 페이지네이션·필터·검색·정렬.
   *
   * ⚠️ 타임라인(`findAll`)과 **다른 엔드포인트**다. 한쪽에 페이지네이션을 얹으면 타임라인이 잘린다.
   */
  findList: (params: PromotionSchedulesListQuery): Promise<PromotionSchedulesListResponse> =>
    SuperFetchV2.get(`${BASE}/list`, {
      params,
      paramsSerializer: (p) => stringify(p, { arrayFormat: 'comma' }),
    }),

  /**
   * 단건 조회 — 상세 페이지가 쓴다. 없는 id·삭제된 id 는 400(PROMOTIONSCHEDULE012).
   *
   * ⚠️ `skipErrorModal` 을 켠다. 상세 화면이 **400 과 그 밖을 나눠 Alert 로 안내**하고 있어
   * 인터셉터 모달까지 뜨면 같은 말을 두 번 하게 된다. 특히 상세에서 삭제한 직후에는
   * 쿼리 무효화로 이 조회가 한 번 더 나가는데, 그때 모달이 뜨면 **삭제 성공 안내보다 먼저**
   * "존재하지 않는 프로모션 스케줄입니다" 가 뜬다.
   */
  findOne: (id: number): Promise<PromotionSchedule> => SuperFetchV2.get(`${BASE}/${id}`, { skipErrorModal: true }),

  /**
   * 엑셀 반출 **요청** — 파일을 받지 않는다.
   *
   * 서버는 큐에 등록만 하고 `{ adminEmail }` 을 즉시 돌려준다.
   * 파일은 excel-worker 가 만들어 그 주소로 **메일 발송**한다.
   */
  requestExcelDownload: (filter: PromotionScheduleListFilter): Promise<PromotionScheduleExcelDownloadResponse> =>
    // @ts-expect-error SuperFetchV2 인터셉터 타입 이슈 (create·update 와 동일 패턴)
    SuperFetchV2.post<Promise<PromotionScheduleExcelDownloadResponse>>(`${BASE}/excel-download`, filter),

  create: (body: PromotionScheduleCreateRequest): Promise<PromotionSchedule> =>
    // @ts-expect-error SuperFetchV2 인터셉터 타입 이슈 (기존 settlementDateAdjustmentApi 와 동일 패턴)
    SuperFetchV2.post<Promise<PromotionSchedule>>(BASE, body),

  update: (id: number, body: PromotionScheduleUpdateRequest): Promise<PromotionSchedule> =>
    // @ts-expect-error SuperFetchV2 인터셉터 타입 이슈
    SuperFetchV2.put<Promise<PromotionSchedule>>(`${BASE}/${id}`, body),

  /** soft delete. 취소 이력을 남기려면 삭제 대신 status=CANCELED 수정을 사용한다. */
  remove: (id: number): Promise<PromotionScheduleDeleteResponse> => SuperFetchV2.delete(`${BASE}/${id}`),

  /**
   * 변경 이력 (최신순 스냅샷).
   *
   * 서버가 먼저 존재를 확인하므로(`findOneOrThrow`) 삭제 직후 무효화에서 `findOne` 과 똑같이
   * 400 을 맞는다 — 이력 탭을 열어둔 채 삭제하면 모달이 **하나 더** 뜨던 경로라 같이 끈다.
   */
  getLogs: (id: number): Promise<PromotionScheduleLog[]> =>
    SuperFetchV2.get(`${BASE}/${id}/logs`, { skipErrorModal: true }),
};
